"use client";

import React from "react";
import { motion } from "framer-motion";
import { Phone, ShieldAlert, Clock, MapPin, FileCheck, CircleAlert as AlertCircle, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
  icon: React.ElementType;
}
const FAQ_ITEMS: FaqItem[] = [{
  id: "response-time",
  question: "What is your typical emergency response time in Prince George's County?",
  answer: "Our local mobile units deploy rapidly across Prince George's County, maintaining an average emergency arrival time of approximately 60 minutes. Crews mobilize via major arteries including the I-495 Capital Beltway, I-95, Route 1, and Route 50 to stabilize water, fire, or mold emergencies promptly.",
  icon: Clock
}, {
  id: "geographic-coverage",
  question: "Which areas and communities in Prince George's County do you serve?",
  answer: "We provide comprehensive coverage across Prince George's County. This includes North County (Laurel, Beltsville, Greenbelt, College Park), Central County (Bowie, Lanham, Hyattsville, Cheverly), and South County (Upper Marlboro, Clinton, Fort Washington, Oxon Hill, Brandywine). We also service residential and commercial properties situated near the Anacostia and Patuxent river watersheds susceptible to localized flooding.",
  icon: MapPin
}, {
  id: "insurance-coordination",
  question: "How does TriServe coordinate with insurance adjusters during a claim?",
  answer: "We work directly alongside your property insurance carrier from initial inspection to final sign-off. Our team generates standardized, itemized moisture readings, thermal imaging records, and structural scope assessments to facilitate transparent documentation and accelerate your claim approval process.",
  icon: FileCheck
}, {
  id: "immediate-steps",
  question: "What immediate steps should a resident take when discovering damage?",
  answer: "For water events, turn off the main water shutoff valve immediately and avoid entering rooms with standing water if electricity is active. For fire or smoke damage, stay clear of compromised structural zones and avoid wiping soot into surfaces. In mold situations, do not disturb visible spores or run standard HVAC fans. Ventilate safe areas if exterior air permits, and call our 24/7 hotline immediately for professional mitigation.",
  icon: AlertCircle
}, {
  id: "mobile-dispatch",
  question: "Does TriServe have a walk-in office location in Maryland?",
  answer: "No. TriServe operates solely through mobile emergency response teams stationed throughout Maryland and the regional corridors. We do not operate a public walk-in customer storefront in Maryland. All equipment, field estimators, and certified technicians deploy directly to your home or commercial site.",
  icon: ShieldAlert
}, {
  id: "emergency-availability",
  question: "Are emergency crews available after hours, weekends, and holidays?",
  answer: "Yes. Our emergency dispatch hotline operates 24 hours a day, 7 days a week, 365 days a year. When you call 855-435-6631, your call routes immediately to our active regional team rather than a detached third-party call center, allowing for immediate assessment and rapid site deployment.",
  icon: HelpCircle
}];
export default function PrinceGeorgesCountyFaq() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map(item => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer
      }
    }))
  };
  return <section id="princegeorgescountyfaq" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-24 border-t border-border overflow-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.32,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center justify-center mb-4">
            <Badge variant="outline" className="text-xs uppercase font-semibold tracking-wider px-3 py-1 rounded-full border-border bg-muted/50 text-foreground">
              LOCAL DISPATCH &amp; SERVICE COVERAGE
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Prince George's County Restoration FAQs
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Essential information regarding mobile dispatch times, insurance claim coordination, and regional emergency mitigation across Prince George's County.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.32,
        delay: 0.08,
        ease: [0.16, 1, 0.3, 1]
      }} className="bg-card text-card-foreground border border-border rounded-xl shadow-xs p-6 sm:p-8 mb-12">
          <Accordion type="single" collapsible className="w-full space-y-2">
            {FAQ_ITEMS.map((item, index) => {
            const Icon = item.icon;
            return <AccordionItem key={item.id} value={item.id} data-index={index} className="border-b border-border/80 last:border-b-0 py-1">
                  <AccordionTrigger className="text-left font-semibold text-base sm:text-lg hover:no-underline hover:text-primary py-4 transition-colors">
                    <span className="flex items-center gap-3">
                      <span className="flex size-7 items-center justify-center rounded-md bg-muted text-foreground/80 shrink-0">
                        <Icon className="size-4" aria-hidden="true" />
                      </span>
                      <span>{item.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pl-10 pr-2 pb-5 text-sm sm:text-base">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>;
          })}
          </Accordion>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.32,
        delay: 0.14,
        ease: [0.16, 1, 0.3, 1]
      }} className="bg-muted/60 border border-border rounded-xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-lg font-semibold text-foreground">
              Have an active emergency in Prince George's County?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our direct regional crew responds 24 hours a day, 7 days a week.
            </p>
          </div>
          <Button asChild size="lg" className="w-full sm:w-auto font-medium px-6 shadow-sm">
            <a href="tel:8554356631" className="flex items-center justify-center gap-2">
              <Phone className="size-4" aria-hidden="true" />
              <span>Call 855-435-6631</span>
            </a>
          </Button>
        </motion.div>
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />
    </section>;
}